import { definedMap, proveNever } from "@glideapps/ts-necessities";
import entries from "lodash/entries";
import { v4 as uuid } from "uuid";
import type {
    AppDescriptionContext,
    ComponentBuilder,
    ComponentDescriptor,
    EnumPropertyCase,
    InteractiveComponentConfiguratorContext,
    PropertyDescriptor,
} from ".";
import { ComponentPrerequisite } from ".";
import { AppKind } from "../../common/components/app-kind";
import type { Currency } from "../../common/components/buy-button-defaults";
import { currencies, findCurrency } from "../../common/components/buy-button-defaults";
import type { AppDescription, SerializedComponent } from "../../common/components/SerializedApp";
import type { Owner, PaymentInformation, PaymentInformationForBuyButtons } from "../../common/Database";
import { getStripeIntegration } from "../../common/Database";
import { logError, logInfo } from "../../common/debug-print";
import type {
    ComponentDescription,
    ComponentKind,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    TableColumn,
    TableGlideType,
    TypeSchema,
} from "../../common/description";
import {
    defaultPaymentMethod,
    findTable,
    getBackendCompatiblePrimitiveColumns,
    getPrimitiveColumns,
    getTableColumn,
    makeTableName,
    PropertyKind,
    ScreenDescriptionKind,
} from "../../common/description";
import { getDocURL } from "../../common/docUrl";
import { rowIndexColumnName } from "../../common/google-sheets";
import { buyScreenName } from "../../component-utils/screen-names";
import { makeText } from "../builder-utils";
import { getPropertyWithColumnFormat } from "../formulas";
import { Appearance } from "./button-appearance";
import { makeCaptionStringPropertyDescriptor } from "./descriptor-utils";
import { ComponentHandlerBase } from "./handler";
import {
    ColumnPropertyFlag,
    ColumnPropertyHandler,
    EnumPropertyHandler,
    SwitchPropertyHandler,
} from "./property-handlers";
import { PropertySection } from "./property-sections";
import { getPaymentMethodProperty } from "./property-utils";

const currencyCases: EnumPropertyCase<string>[] = currencies.map(({ code: symbol, name }) => ({
    value: symbol,
    label: `${symbol.toUpperCase()} — ${name}`,
}));

const nameProperty = new ColumnPropertyHandler(
    "nameProperty",
    "Name",
    [ColumnPropertyFlag.Required, ColumnPropertyFlag.Editable, ColumnPropertyFlag.Searchable],
    undefined,
    ["name", "title", "product", "item"],
    getBackendCompatiblePrimitiveColumns,
    "string",
    PropertySection.PaymentProductInfo
);
const descriptionProperty = new ColumnPropertyHandler(
    "descriptionProperty",
    "Description",
    [ColumnPropertyFlag.Editable, ColumnPropertyFlag.Searchable],
    undefined,
    ["description"],
    getPrimitiveColumns,
    "string",
    PropertySection.PaymentProductInfo
);
const imageProperty = new ColumnPropertyHandler(
    "imageProperty",
    "Image",
    [ColumnPropertyFlag.Editable],
    undefined,
    ["image"],
    getPrimitiveColumns,
    "image-uri",
    PropertySection.PaymentProductInfo
);
const skuProperty = new ColumnPropertyHandler(
    "skuProperty",
    "Product ID",
    [ColumnPropertyFlag.Required, ColumnPropertyFlag.Editable, ColumnPropertyFlag.Searchable],
    undefined,
    ["sku", "id"],
    getBackendCompatiblePrimitiveColumns,
    "string",
    PropertySection.PaymentProductInfo
);
const priceProperty = new ColumnPropertyHandler(
    "priceProperty",
    "Price",
    [ColumnPropertyFlag.Required, ColumnPropertyFlag.Editable],
    undefined,
    ["price", "cost"],
    getBackendCompatiblePrimitiveColumns,
    "number",
    PropertySection.PaymentProductInfo
);
const currencyHandler = new EnumPropertyHandler(
    {
        currency: "usd",
    },
    "Currency",
    "Currency",
    currencyCases,
    PropertySection.Options,
    "dropdown"
);
const shoppingCartHandler = new SwitchPropertyHandler(
    { addToShoppingCart: false },
    "Add to shopping cart",
    PropertySection.Options
);
const withShippingHandler = new SwitchPropertyHandler(
    { withShipping: true },
    "Require shipping address",
    PropertySection.Options
);
const showStripeLogoHandler = new SwitchPropertyHandler(
    { showStripeLogo: false },
    "Display Stripe logo below button",
    PropertySection.Options
);

export const ComponentKindBuyButton: ComponentKind = "buy-button";

export interface BuyButtonComponentDescription extends ComponentDescription {
    readonly paymentMethod: PropertyDescription | undefined;
    readonly nameProperty: PropertyDescription;
    readonly descriptionProperty: PropertyDescription | undefined;
    readonly imageProperty: PropertyDescription | undefined;
    readonly skuProperty: PropertyDescription;
    readonly priceProperty: PropertyDescription;
    readonly currency: PropertyDescription | undefined;
    readonly addToShoppingCart: PropertyDescription | undefined;
    readonly withShipping: PropertyDescription;
    readonly caption: PropertyDescription | undefined;
    // FIXME: Why is this not returned by `getComponentID`?
    readonly id: string;
}

export class BuyButtonComponentHandler extends ComponentHandlerBase<BuyButtonComponentDescription> {
    public readonly appKinds = AppKind.App;

    constructor() {
        super(ComponentKindBuyButton);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: BuyButtonComponentDescription,
        { input }: InputOutputTables
    ): SerializedComponent {
        const { b } = builder;

        const nameColumn = nameProperty.getColumnName(desc);
        const descriptionColumn = descriptionProperty.getColumnName(desc);
        const imageColumn = imageProperty.getColumnName(desc);
        const skuColumn = skuProperty.getColumnName(desc);
        const priceColumn = priceProperty.getColumnName(desc);
        const addToCart = shoppingCartHandler.getSwitch(desc);
        const withShipping = withShippingHandler.getSwitch(desc);
        const showStripeLogo = showStripeLogoHandler.getSwitch(desc);

        if (nameColumn === undefined || skuColumn === undefined || priceColumn === undefined) {
            return b.emptyUI;
        }

        const itemName = getPropertyWithColumnFormat(builder, input, nameColumn);
        const itemDescription = definedMap(descriptionColumn, c => getPropertyWithColumnFormat(builder, input, c));
        const itemImage = definedMap(imageColumn, n => b.getProperty(n, b.getContext, input));
        const itemSKU = b.getProperty(skuColumn, b.getContext, input);
        const itemPrice = b.getProperty(priceColumn, b.getContext, input);
        const rowIndex = b.getProperty(rowIndexColumnName, b.getContext, input);

        const item = b.object({
            itemName,
            itemDescription,
            itemImage,
            itemSKU,
            itemPrice,
            currencyCode: b.constant(currencyHandler.getEnum(desc)),
            withShipping: b.constant(withShipping),
            showStripeLogo: b.constant(showStripeLogo),
            buttonID: b.constant(desc.id),
            rowIndex,
        });
        let onTap: SerializedComponent;
        if (addToCart) {
            onTap = b.sequence([
                b.addRowToTable("local", makeTableName("shopping-cart"), item, undefined, undefined),
                b.showToast(b.localizedString("added"), true),
            ]);
        } else {
            onTap = b.pushModalBuilderScreen(
                buyScreenName,
                b.object({
                    items: b.list([item]),
                    onFinished: b.popModalScreen,
                    onSuccess: b.noAction(),
                }),
                b.localizedString("checkout"),
                true,
                true
            );
        }

        const caption =
            makeText(builder, desc.caption, input, true) ??
            b.localizedString(addToCart ? ("addToCart" as const) : ("buy" as const));
        return b.if(
            b.and(b.isDefined(itemName), b.and(b.isDefined(itemSKU), b.isDefined(itemPrice))),
            b.button(caption, Appearance.Filled, undefined, undefined, onTap, {}),
            b.emptyUI
        );
    }

    public getDescriptor(
        desc: BuyButtonComponentDescription | undefined,
        _table: TableGlideType | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor {
        const baseDescriptor: ComponentDescriptor = {
            name: "Buy Button",
            description: "Sell goods or services",
            img: "componentBuyButton",
            group: "Buttons",
            helpUrl: getDocURL("buyButton"),
            isLegacy: !ccc.eminenceFlags.buyButton,
            properties: [
                {
                    kind: PropertyKind.PaymentMethod,
                    property: { name: "paymentMethod" },
                    label: "Payment method",
                    section: PropertySection.PaymentProcessor,
                },
                ...this.getBasePropertyDescriptors(),
            ],
        };

        // If the payment method is not defined, we need to have the user set it before going forward.
        if (desc === undefined || desc.paymentMethod === undefined) {
            return baseDescriptor;
        }

        const properties: PropertyDescriptor[] = [
            ...baseDescriptor.properties,
            nameProperty,
            descriptionProperty,
            imageProperty,
            skuProperty,
            priceProperty,
            currencyHandler,
            shoppingCartHandler,
            withShippingHandler,
            showStripeLogoHandler,
            makeCaptionStringPropertyDescriptor("Buy", false, mutatingScreenKind, {
                propertySection: PropertySection.Top,
                placeholder: "ex: Buy Now",
            }),
        ];

        return { ...baseDescriptor, properties };
    }

    public newComponent(
        tables: InputOutputTables,
        usedColumns: ReadonlySet<TableColumn>,
        editedColumns: ReadonlySet<TableColumn>,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): BuyButtonComponentDescription | undefined {
        const desc = super.newComponent(tables, usedColumns, editedColumns, iccc, mutatingScreenKind);
        if (desc === undefined) return undefined;

        return {
            ...desc,
            id: uuid(),
        };
    }

    protected duplicateComponentBase(desc: BuyButtonComponentDescription): BuyButtonComponentDescription {
        return { ...super.duplicateComponentBase(desc), id: uuid() };
    }

    public get prerequisites(): readonly ComponentPrerequisite[] {
        return [ComponentPrerequisite.StripeConnect];
    }
}

export interface BuyButtonColumns {
    readonly nameColumn: TableColumn;
    readonly skuColumn: TableColumn;
    readonly priceColumn: TableColumn;
    readonly currency: Currency;
}

export function getColumns(desc: BuyButtonComponentDescription, table: TableGlideType): BuyButtonColumns | undefined {
    const nameColumnName = nameProperty.getColumnName(desc);
    const skuColumnName = skuProperty.getColumnName(desc);
    const priceColumnName = priceProperty.getColumnName(desc);

    if (nameColumnName === undefined || skuColumnName === undefined || priceColumnName === undefined) return undefined;

    const nameColumn = getTableColumn(table, nameColumnName);
    const skuColumn = getTableColumn(table, skuColumnName);
    const priceColumn = getTableColumn(table, priceColumnName);

    if (nameColumn === undefined || skuColumn === undefined || priceColumn === undefined) return undefined;

    const currency = findCurrency(currencyHandler.getEnum(desc));
    if (currency === undefined) return undefined;

    return { nameColumn, skuColumn, priceColumn, currency };
}

interface BuyButtonInfo {
    readonly buyButton: BuyButtonComponentDescription;
    readonly table: TableGlideType;
}

export function getBuyButtons(appDescription: AppDescription, schema: TypeSchema): readonly BuyButtonInfo[] {
    const buyButtons: BuyButtonInfo[] = [];

    for (const [screenName, screenDesc] of entries(appDescription.screenDescriptions)) {
        if (screenDesc.kind !== ScreenDescriptionKind.Class) continue;

        for (const desc of screenDesc.components) {
            if (desc.kind !== ComponentKindBuyButton) continue;

            const buyButton = desc as BuyButtonComponentDescription;

            const table = findTable(schema, screenDesc.type);
            if (table === undefined) {
                logError("Could not find table for buy button", screenName, screenDesc.type);
                continue;
            }

            buyButtons.push({ buyButton, table });
        }
    }

    logInfo("Number of buy buttons", buyButtons.length);

    return buyButtons;
}

export function getPaymentInformationForBuyButtons(
    appDescription: AppDescription,
    schema: TypeSchema,
    owner: Owner,
    withTestMode: boolean
): PaymentInformationForBuyButtons {
    const integration = getStripeIntegration(owner);
    if (integration === undefined) {
        logInfo("No Stripe integration");
        return {};
    }

    const infos: { [buyButtonID: string]: PaymentInformation } = {};
    const { stripeUserID, livePublishableKey, testPublishableKey } = integration;
    for (const { buyButton } of getBuyButtons(appDescription, schema)) {
        const paymentMethod = getPaymentMethodProperty(buyButton.paymentMethod) ?? defaultPaymentMethod;
        let { processor } = paymentMethod;
        if (processor !== "stripe") {
            processor = proveNever(processor, "Invalid processor on buy button payment method", "stripe");
        }

        infos[buyButton.id] = withTestMode
            ? { processor: "stripe", stripeUserID, livePublishableKey, testPublishableKey }
            : { processor: "stripe", stripeUserID };
    }

    return infos;
}
